'use strict';

var themes = require('./themes.js');

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Sort an array of strings in a special way that considers numeric parts within the strings.
 *
 * @param values - An array of strings to be sorted.
 * @returns - A new array containing the sorted strings.
 */
function sortNumericValues(values) {
    // Create a copy of the input array to avoid modifying the original array.
    return __spreadArray([], values, true).sort(function (a, b) {
        return a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    });
}

function extractTokens(tokens, prefix) {
    return tokens.filter(function (token) {
        return token.startsWith(prefix);
    });
}
function excludeTokens(tokens, prefix) {
    return tokens.filter(function (token) {
        return !token.startsWith(prefix);
    });
}
var ALL_TOKENS = Object.keys(themes.LoadsmartTheme);
// We strip brand and platform-specific tokens as they should not be used directly.
var TOKENS = sortNumericValues(excludeTokens(excludeTokens(ALL_TOKENS, 'brand-'), 'platform-'));
var BACKGROUND_COLOR_TOKENS = extractTokens(TOKENS, 'color-background-');
var BORDER_RADIUS_TOKENS = extractTokens(TOKENS, 'border-radius-');
var BORDER_TOKENS = extractTokens(TOKENS, 'border-');
var BORDER_WIDTH_TOKENS = [
    'border-none',
    'border-thin',
    'border-medium',
    'border-thick',
];
var COLOR_TOKENS = extractTokens(TOKENS, 'color-');
var ELEVATION_TOKENS = extractTokens(TOKENS, 'elevation-');
var FONT_FAMILY_TOKENS = extractTokens(TOKENS, 'font-family-');
var FONT_SIZE_TOKENS = extractTokens(TOKENS, 'font-size-');
var FONT_WEIGHT_TOKENS = extractTokens(TOKENS, 'font-weight-');
var GLOBAL_TOKENS = extractTokens(TOKENS, 'global-');
var GLOW_TOKENS = extractTokens(TOKENS, 'glow-');
var LINE_HEIGHT_TOKENS = extractTokens(TOKENS, 'line-height-');
var OPACITY_TOKENS = extractTokens(TOKENS, 'opacity-');
var SPACING_TOKENS = extractTokens(TOKENS, 'spacing-');
var MEDIA_QUERY_TOKENS = extractTokens(TOKENS, 'media-');
var SPACING_TOKENS_WITH_NONE = __spreadArray(['none'], SPACING_TOKENS, true);

/**
 * Get token value.
 *
 * @example
 * ```js
 * getToken('color-primary')
 * ```
 *
 * @param token - Token whose value should be retrieved.
 * @returns {TokenValue | undefined} Token value or `undefined` otherwise.
 */
function getToken(token) {
    return themes.LoadsmartTheme[token];
}

function toCSSVariable(token) {
    var value = getToken(token);
    if (value == null) {
        return value;
    }
    return "--m-".concat(token);
}

function toCSSValue(token, alpha) {
    var value = getToken(token);
    if (value == null) {
        return '';
    }
    if (token.startsWith('color-')) {
        return "rgba(var(".concat(toCSSVariable(token), ", ").concat(value, "), ").concat(alpha !== null && alpha !== void 0 ? alpha : 1, ")");
    }
    return "var(".concat(toCSSVariable(token), ", ").concat(value, ")");
}

/**
 * Get css value for spacing token validating 'none'.
 *
 * @param spacing - Spacing token or 'none'
 * @param defaultSpacing - Default spacing token if the provided spacing is not valid
 * @returns The css value for the spacing token
 */
function toSpacingCSSValue(spacing, defaultSpacing) {
    var _a;
    var defaultValue = defaultSpacing ? toCSSValue(defaultSpacing) : '';
    if (!spacing) {
        return defaultValue;
    }
    return (_a = (spacing === 'none' ? '0' : toCSSValue(spacing))) !== null && _a !== void 0 ? _a : defaultValue;
}

/**
 * Get the hex value for a color token.
 *
 * @example
 * ```js
 * getHexColor('color-primary')
 * ```
 *
 * @param token - Color token whose value should be retrieved.
 * @returns {string | undefined} Hexadecimal color value or undefined
 */
function getHexColor(token) {
    var tokenValue = String(getToken(token) || '');
    if (!tokenValue)
        return undefined;
    var colors = tokenValue.split(', ').map(Number);
    if (colors.length !== 3)
        throw new Error('color token must be in the `R, G, B` format');
    return colors.reduce(function (hexColor, current) {
        var hexValue = current.toString(16).padStart(2, '0');
        return hexColor.concat(hexValue);
    }, '#');
}

exports.BACKGROUND_COLOR_TOKENS = BACKGROUND_COLOR_TOKENS;
exports.BORDER_RADIUS_TOKENS = BORDER_RADIUS_TOKENS;
exports.BORDER_TOKENS = BORDER_TOKENS;
exports.BORDER_WIDTH_TOKENS = BORDER_WIDTH_TOKENS;
exports.COLOR_TOKENS = COLOR_TOKENS;
exports.ELEVATION_TOKENS = ELEVATION_TOKENS;
exports.FONT_FAMILY_TOKENS = FONT_FAMILY_TOKENS;
exports.FONT_SIZE_TOKENS = FONT_SIZE_TOKENS;
exports.FONT_WEIGHT_TOKENS = FONT_WEIGHT_TOKENS;
exports.GLOBAL_TOKENS = GLOBAL_TOKENS;
exports.GLOW_TOKENS = GLOW_TOKENS;
exports.LINE_HEIGHT_TOKENS = LINE_HEIGHT_TOKENS;
exports.MEDIA_QUERY_TOKENS = MEDIA_QUERY_TOKENS;
exports.OPACITY_TOKENS = OPACITY_TOKENS;
exports.SPACING_TOKENS = SPACING_TOKENS;
exports.SPACING_TOKENS_WITH_NONE = SPACING_TOKENS_WITH_NONE;
exports.TOKENS = TOKENS;
exports.getHexColor = getHexColor;
exports.getToken = getToken;
exports.toCSSValue = toCSSValue;
exports.toCSSVariable = toCSSVariable;
exports.toSpacingCSSValue = toSpacingCSSValue;
